/**
 * ConfirmDeleteDialog — "type to confirm" delete dialog.
 *
 * Pattern used by GitHub, Stripe, Cloudflare etc. for destructive
 * actions on high-value records. Broker must type the entity's name
 * (or the literal word "DELETE") into a text field before the Delete
 * button unlocks. Case-insensitive. Prevents accidental clicks from
 * wiping a contact.
 *
 * Usage:
 *   const [open, setOpen] = useState(false)
 *   ...
 *   <button onClick={() => setOpen(true)}>Delete</button>
 *   <ConfirmDeleteDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     title="Delete contact"
 *     description="This will permanently remove Tom Daly + all their deals + communications history."
 *     confirmationText="DELETE"
 *     onConfirm={async () => await remove.mutateAsync()}
 *   />
 */
import { useEffect, useState } from "react"
import { AlertTriangle, Loader2, Trash2 } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

export function ConfirmDeleteDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmationText,
  onConfirm,
  destructiveLabel = "Delete",
  additionalWarning,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  title: string
  description: React.ReactNode
  /** The exact string the user must type. Case-insensitive match.
   *  Recommended: the entity name (e.g. "Tom Daly") for maximum friction. */
  confirmationText: string
  onConfirm: () => Promise<unknown> | unknown
  destructiveLabel?: string
  /** Optional extra red-text warning below the description (e.g.
   *  "5 open deals, 12 communications will also be deleted"). */
  additionalWarning?: React.ReactNode
}) {
  const [typed, setTyped] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset the input whenever the dialog opens/closes so a stale value can't
  // pre-satisfy the check on a subsequent open.
  useEffect(() => {
    if (!open) {
      setTyped("")
      setError(null)
      setBusy(false)
    }
  }, [open])

  const matches = typed.trim().toLowerCase() === confirmationText.trim().toLowerCase()

  const handleConfirm = async () => {
    if (!matches || busy) return
    setBusy(true)
    setError(null)
    try {
      await onConfirm()
      onOpenChange(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed. Try again.")
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-900">
            <AlertTriangle className="h-5 w-5 text-rose-600" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
            {description}
            {additionalWarning && (
              <div className="mt-2 border-t border-rose-200 pt-2 text-xs font-medium">
                {additionalWarning}
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="confirm-delete-input" className="mb-1.5 block">
              Type <span className="font-mono font-semibold text-rose-700">{confirmationText}</span>{" "}
              to confirm
            </Label>
            <Input
              id="confirm-delete-input"
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && matches) handleConfirm()
              }}
              placeholder={confirmationText}
              className="font-mono"
              disabled={busy}
            />
          </div>

          {error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
              {error}
            </div>
          )}
        </div>

        <div className="mt-2 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!matches || busy}
            className="bg-rose-600 hover:bg-rose-700 focus-visible:ring-rose-500"
          >
            {busy ? (
              <>
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                Deleting…
              </>
            ) : (
              <>
                <Trash2 className="mr-1.5 h-4 w-4" />
                {destructiveLabel}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
